import { Box, makeStyles, Typography } from '@material-ui/core'
import CommentIcon from '@material-ui/icons/Comment'
import { ExistingStockProvider } from 'components/domain/csv/ExistingStockProvider'
import { Condition, CustomTooltip } from 'components/facets'
import { LanguageFlag } from 'components/facets/LanguageFlag'
import { useUser } from 'components/providers/UserProvider'
import { theme } from 'config'
import { useLocalStorageState } from 'lib/hooks'
import { useStockStore } from 'lib/hooks/useStockStore'
import { currencySymbol, exposeToCypress } from 'lib/utils'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AppCurrency,
  CardLanguage,
  CountryEdition,
  FINISH_TYPE_LABELS,
  flagCode,
  InventoryArticle,
  TAXATION_SCHEME,
  Template,
} from 'shared'
import {
  FIRST_EDITION_ICON,
  FOIL_ICON,
  PLAYSET_ICON,
  REVERSE_HOLO_ICON,
  SIGNED_ICON,
} from 'utils/constants'
import { ArticleTemplate } from '../ArticleTemplate/ArticleTemplate'
// import { ArticleTemplateDialog } from '../ArticleTemplateDialog'
import { AddSingleArticlesByExp } from './AddSingleArticlesByExp'
import { BulkAddByExp } from './BulkAddByExp'
import { ProfiAddArticles } from './ProfiAddArticles'

export interface AddArticleDockProps {
  disableHotkeys: boolean
  discardChange: boolean
  onAddArticles: (articles: InventoryArticle[]) => void
  showThisTab: 'addByCard' | 'addByExp' | 'addBulk'
  handleBackToMenu: () => void
}

interface DefaultValueProps {
  template: Template
  currency?: AppCurrency
}

const useStyles = makeStyles(() => ({
  bottomNav: {
    backgroundColor: 'transparent',
    minWidth: '60px',
  },
  headerTitle: {
    position: 'relative',
    display: 'flex',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  typeImportBox: {
    marginTop: '-12px',
  },
  typeImportText: {
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontSize: '19px',
  },
  iconBox: {
    position: 'absolute',
    textAlign: 'center',
    top: '-32px',
    right: '0',
  },
  defautValuesFontSize: {
    fontSize: '13px',
  },
  defautValuesImgHeight: {
    marginTop: '2px',
    height: '15px',
  },
  defautValuesAttributesBox: {
    paddingLeft: '3px',
    paddingRight: '3px',
  },
  defaultValueContainer: {
    minHeight: '40px',
    borderRadius: '10px',
    marginLeft: '5px',
    marginRight: '5px',
    display: 'flex',
    padding: '10px',
  },
}))

export const AddArticleDock = ({
  onAddArticles,
  disableHotkeys,
  showThisTab,
  discardChange,
  handleBackToMenu,
}: AddArticleDockProps): JSX.Element => {
  const [selectedTab, setSelectedTab] = useState<'addByCard' | 'addByExp' | 'addBulk'>(showThisTab)
  const { t } = useTranslation()
  const classes = useStyles()
  const activeStockType = useStockStore((state) => state.activeStockType)
  const { user } = useUser()

  useEffect(() => {
    setSelectedTab(showThisTab)
  }, [showThisTab])

  const taxationSchemeFromUser = (): number | undefined => {
    if (!user.featureFlags?.taxation) return undefined
    if (!user?.defaultTaxationSchema) return undefined
    return activeStockType === 'singles'
      ? user.defaultTaxationSchema.singles
      : user.defaultTaxationSchema.sealed
  }

  const defaultTemplate: Template = {
    condition: 'NM',
    idLanguage: CardLanguage.English,
    isPlayset: false,
    isFirstEd: false,
    isSigned: false,
    isAltered: false,
    comment: '',
    location: '',
    quantity: 1,
    price: 1000,
    countryEdition: CountryEdition.Regular,
    grade: undefined,
    gradingCompany: undefined,
    finishType: FINISH_TYPE_LABELS.REGULAR,
    taxationScheme: taxationSchemeFromUser(),
    buyPrice: undefined,
    standardTaxationQuantity: taxationSchemeFromUser() === TAXATION_SCHEME.STANDARD ? 1 : 0,
    marginTaxationQuantity: taxationSchemeFromUser() === TAXATION_SCHEME.MARGIN ? 1 : 0,
  }

  const [template, setTemplate] = useLocalStorageState<Template>(defaultTemplate, 'template')

  // Sync taxation scheme from user settings when it changes
  const currentScheme = taxationSchemeFromUser()
  useEffect(() => {
    if (currentScheme === undefined) return
    if (template.taxationScheme === currentScheme) return
    setTemplate({
      ...template,
      taxationScheme: currentScheme,
      standardTaxationQuantity: currentScheme === TAXATION_SCHEME.STANDARD ? template.quantity : 0,
      marginTaxationQuantity: currentScheme === TAXATION_SCHEME.MARGIN ? template.quantity : 0,
    })
  }, [currentScheme])

  exposeToCypress({ articleTemplate: template })

  return (
    <>
      <div>
        <Box className={classes.headerTitle} marginTop={2} sx={{ justifyContent: 'center' }}>
          {showThisTab === 'addByCard' && (
            <div className="d-flex align-items-center">
              <i
                data-testid="back-to-menu"
                className="fa-solid fa-arrow-left mx-2"
                onClick={handleBackToMenu}
                style={{ fontSize: '20px', color: 'var(--primary-color)' }}
              ></i>
              <Typography className={classes.typeImportText}>
                {activeStockType === 'singles'
                  ? t('addArticles.sigleCardTitleImportButton')
                  : t('addArticles.sigleItemTitleImportButton')}
              </Typography>
            </div>
          )}

          {showThisTab === 'addByExp' && (
            <div className="d-flex align-items-center">
              <i
                data-testid="back-to-menu"
                className="fa-solid fa-arrow-left mx-2"
                onClick={handleBackToMenu}
                style={{ fontSize: '20px', color: 'var(--primary-color)' }}
              ></i>
              <Typography className={classes.typeImportText}>
                {t('addArticles.byExpansionTitleImportButton')}
              </Typography>
            </div>
          )}

          {showThisTab === 'addBulk' && (
            <div className="d-flex align-items-center">
              <i
                data-testid="back-to-menu"
                className="fa-solid fa-arrow-left mx-2"
                onClick={handleBackToMenu}
                style={{ fontSize: '20px', color: 'var(--primary-color)' }}
              ></i>
              <Typography className={classes.typeImportText}>
                {t('addArticles.bulkTitleImportButton')}
              </Typography>
            </div>
          )}
        </Box>
      </div>
      <ExistingStockProvider>
        <Box padding={1}>
          {selectedTab === 'addByCard' && (
            <ProfiAddArticles
              disableHotkeys={disableHotkeys}
              onAddArticles={onAddArticles}
              template={template}
              discardChange={discardChange}
            />
          )}
          {selectedTab === 'addByExp' && (
            <AddSingleArticlesByExp
              disableHotkeys={disableHotkeys}
              onAddArticles={onAddArticles}
              template={template}
            />
          )}
          {selectedTab === 'addBulk' && (
            <BulkAddByExp onAddArticles={onAddArticles} template={template} />
          )}
          <div className="mt-4 p-1 pb-3">
            <p className="fs-6 fw-semibold">{t('addArticles.defaultValues')}</p>
            <ArticleTemplate setTemplate={setTemplate} template={template} />
          </div>
        </Box>
      </ExistingStockProvider>
    </>
  )
}

export const DefaultValue = ({ template, currency = 'EUR' }: DefaultValueProps): JSX.Element => {
  const classes = useStyles()

  return (
    <>
      <Box style={{ display: 'flex', marginTop: '2px' }}>
        <LanguageFlag
          style={{
            paddingRight: '3px',
            marginTop: '3px',
          }}
          height="15px"
          code={flagCode(template.idLanguage) || 'gg'}
        />

        {template.finishType === FINISH_TYPE_LABELS.FOIL && (
          <Box
            className={classes.defautValuesImgHeight}
            style={{ marginTop: '-1px', marginLeft: '2px', marginRight: '2px' }}
          >
            <img alt="foil" src={FOIL_ICON} style={{ width: '18px' }} />
          </Box>
        )}

        {template.finishType === FINISH_TYPE_LABELS.REVERSE_HOLO && (
          <Box className={classes.defautValuesAttributesBox}>
            <img
              alt="reverse holo"
              className={classes.defautValuesImgHeight}
              src={REVERSE_HOLO_ICON}
            />
          </Box>
        )}

        {template.isPlayset && (
          <Box className={classes.defautValuesAttributesBox}>
            <img alt="playset" className={classes.defautValuesImgHeight} src={PLAYSET_ICON} />
          </Box>
        )}
        {template.isFirstEd && (
          <Box className={classes.defautValuesAttributesBox}>
            <img
              alt="first edition"
              src={FIRST_EDITION_ICON}
              className={classes.defautValuesImgHeight}
            />
          </Box>
        )}
        {template.isSigned && (
          <Box className={classes.defautValuesAttributesBox}>
            <img alt="signed" className={classes.defautValuesImgHeight} src={SIGNED_ICON} />
          </Box>
        )}

        {template.location && (
          <CustomTooltip title={<Typography variant="body1">{template.location}</Typography>}>
            <CommentIcon
              data-testid={``}
              style={{
                fontSize: `21px`,
                marginLeft: '3px',
              }}
            />
          </CustomTooltip>
        )}

        {template.comment && (
          <CustomTooltip title={<Typography variant="body1">{template.comment}</Typography>}>
            <CommentIcon
              data-testid={``}
              style={{
                fontSize: `21px`,
                marginLeft: '3px',
              }}
            />
          </CustomTooltip>
        )}
      </Box>

      <Box style={{ display: 'flex', marginTop: '3px' }}>
        <Box style={{ fontSize: '14px', paddingRight: '3px' }}>
          <Condition id="AddArticleDock-condition" value={template.condition} noPadding={true} />
        </Box>

        <Typography className={classes.defautValuesFontSize}>
          {`| ${template.quantity}x | ${template.price}${currencySymbol(currency)} `}
        </Typography>
      </Box>
    </>
  )
}
